import axios from '../../../axios.conf';
import secureLocalStorage from 'react-secure-storage';
import { filterEmptyValues, processSupplierOperations } from '../../../utils/payloadUtils';
import { history } from '../../../components/Helpers';
import { toast as globalToast } from '../../../components/ToastManager';
import { handleExecutionLog } from '../../../components/ExecutionLogHandler';
import { fetchStart, fetchEnd } from '../Simulations/OperationalReports/AuthAction';
import {
  FETCH_MATERIAL_UPDATES_START,
  FETCH_MATERIAL_UPDATES_SUCCESS,
  FETCH_MATERIAL_UPDATES_FAIL,
  FETCH_SINGLE_MATERIAL_UPDATE_START,
  FETCH_SINGLE_MATERIAL_UPDATE_SUCCESS,
  FETCH_SINGLE_MATERIAL_UPDATE_FAIL,
  CREATE_MATERIAL_UPDATE_START,
  CREATE_MATERIAL_UPDATE_SUCCESS,
  CREATE_MATERIAL_UPDATE_FAIL,
  UPDATE_MATERIAL_UPDATE_START,
  UPDATE_MATERIAL_UPDATE_SUCCESS,
  UPDATE_MATERIAL_UPDATE_FAIL,
  DELETE_MATERIAL_UPDATE_START,
  DELETE_MATERIAL_UPDATE_SUCCESS,
  DELETE_MATERIAL_UPDATE_FAIL,
  CLEAR_CURRENT_UPDATE,
  CLEAR_MASTER_DATA_UPDATES_CACHE,
  FETCH_MASTER_DATA_UPDATES_BY_PROFILE,
  FETCH_MASTER_DATA_UPDATES_BY_PROFILE_SUCCESS,
  FETCH_MASTER_DATA_UPDATES_BY_PROFILE_FAIL,
  SUPPLIER_DETAILS_BY_UPDATE_NO,
  SUPPLIER_DETAILS_BY_UPDATE_NO_FAIL,
  SET_MESSAGE_DIALOG_DATA,
  FETCH_CUSTOMER_MASTER_START,
  FETCH_CUSTOMER_MASTER_SUCCESS,
  FETCH_CUSTOMER_MASTER_FAIL,
  FETCH_CUSTOMER_MASTER_BY_ID_START,
  FETCH_CUSTOMER_MASTER_BY_ID_SUCCESS,
  FETCH_CUSTOMER_MASTER_BY_ID_FAIL,
  FETCH_APPROVAL_PAGE_DETAILS,
  FETCH_CUSTOMER_UPDATE_DEFAULT_PROFILE_START,
  FETCH_CUSTOMER_UPDATE_DEFAULT_PROFILE_SUCCESS,
  FETCH_CUSTOMER_UPDATE_DEFAULT_PROFILE_FAIL,
  FETCH_CUSTOMER_MASTER_GROUPED_START,
  FETCH_CUSTOMER_MASTER_GROUPED_SUCCESS,
  FETCH_CUSTOMER_MASTER_GROUPED_FAIL,
  CLEAR_FETCH_CUSTOMER_MASTER_BY_ID,
  GET_MASTER_DATA_PROFILE,
  CLEAR_MASTER_DATA_PROFILE,
  GET_MASTER_DATA_PROFILE_BY_NAME_SUCCESS,
  GET_CUSTOMER_BY_PROFILE_SUCCESS,
  CLEAR_CUSTOMER_BY_PROFILE,
  CLONE_CUSTOMER_MASTER_SUCCESS,
} from './ActionTypes';

const capitalizeFirstLetter = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

// API call functions
const getMaterialUpdatesAPI = (page = 1, limit = 10, type) => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk')
  };
  // Switch endpoint based on type parameter
  const endpoint = type === 'supplier' ? 'supplier-update' : 'material-update';
  return axios.get(`mdm/${endpoint}?page=${page}&limit=${limit}`, { headers });
};


const updateMaterialUpdateAPI = (id, data, type = 'material') => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk'),
    'Content-Type': 'application/json'
  };

  // Log the request payload for debugging
  //console.log('Update payload received:', JSON.stringify(data, null, 2));

  if (type === 'supplier') {
    // Exact payload format for supplier updates as specified
    const supplierPayload = {
      profile_name: data.profile_name || "Global_supplier",
      supplier_name: data.supplier_name || data.supplier_description || "asd",
      effective_date: data.effective_date || new Date().toISOString().split('T')[0],
      internal_status: data.internal_status || "new",
      supplier_category: data.supplier_category || "Supplier",
      approval_status: data.approval_status,
      header_update_type: data.header_update_type || "CREATE",
      // Always include supplier_number, even if it's an empty string
      supplier_number: data.supplier_number || "",
      image_url: data.image_url || "",
      updates: []
    };

    // Helper function to filter out empty values from an object
    const filterEmptyValues = (obj) => {
      if (!obj || typeof obj !== 'object') return {};

      const result = {};
      Object.entries(obj).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          result[key] = value;
        }
      });
      return result;
    };

    // Helper function to process operations and filter out empty values
    const processOperations = (operations) => {
      if (!Array.isArray(operations)) return [];

      return operations
        .map(op => {
          // Handle new format: operations with data property (no operation field)
          if (op.data && !op.operation) {
            if (op.delete_row === true) {
              const pressedIcon = localStorage.getItem('pressedIcon');
              if (data.header_update_type === 'CREATE' || pressedIcon === 'createButton') {
                return null;
              }
              return { data: op.data || {}, delete_row: true };
            }
            const filteredData = filterEmptyValues(op.data);
            return Object.keys(filteredData).length > 0 ? { data: filteredData } : null;
          }

          // For add_row operations
          if (op.operation === 'add_row' && op.data) {
            const filteredData = filterEmptyValues(op.data);
            return Object.keys(filteredData).length > 0 ? { data: filteredData } : null;
          }

          // For update_row operations
          if (op.operation === 'update_row') {
            const updates = op.updates || op.data;
            if (!updates) return null;

            const filteredUpdates = filterEmptyValues(updates);
            return Object.keys(filteredUpdates).length > 0 ?
              { row_id: op.row_id, updates: filteredUpdates } : null;
          }

          // For delete operations
          if (op.operation === 'delete_row') {
            const pressedIcon = localStorage.getItem('pressedIcon');
            if (data.header_update_type === 'CREATE' || pressedIcon === 'createButton') {
              return null;
            }
            return {
              data: op.data || {},
              delete_row: true
            };
          }

          if (op.operation === 'delete_field' && op.field_name) {
            return { row_id: op.row_id, field_name: op.field_name };
          }

          return null;
        })
        .filter(item => item !== null); // Remove null items
    };

    // Handle the updates array structure
    if (data.updates && Array.isArray(data.updates)) {
      // Data is already in the correct format with updates array
      supplierPayload.updates = data.updates.map(update => {
        // Extract operations from update_details (already an array)
        const operations = Array.isArray(update.update_details)
          ? update.update_details
          : [];

        // Process operations to only include changed items
        const updateDetails = processOperations(operations);

        return {
          view: update.view,
          attribute_value: update.attribute_value,
          update_details: updateDetails.length > 0 ? updateDetails : []
        };
      }).filter(update => update.update_details.length > 0); // Filter out empty updates
    } else if (data.update_details && Array.isArray(data.update_details.view_data_operations)) {
      // Group operations by attribute_value
      const operationsByAttribute = {};

      // Include all operations, including delete operations
      data.update_details.view_data_operations.forEach(op => {
        // Skip operations with empty data, but always include delete operations
        if (op.operation === 'add_row' && (!op.data || Object.keys(filterEmptyValues(op.data)).length === 0)) {
          return;
        }
        if (op.operation === 'update_row' &&
          (!op.updates || Object.keys(filterEmptyValues(op.updates || op.data)).length === 0)) {
          return;
        }
        // Always include delete operations regardless of data content
        // (no early return for delete operations)

        const attributeValue = op.attribute_value || 0;
        if (!operationsByAttribute[attributeValue]) {
          operationsByAttribute[attributeValue] = [];
        }
        operationsByAttribute[attributeValue].push(op);
      });

      // Create updates array from grouped operations
      Object.keys(operationsByAttribute).forEach(attributeValue => {
        const operations = operationsByAttribute[attributeValue];
        const updateDetails = processOperations(operations);

        // Only add to updates if there are actual changes
        if (updateDetails.length > 0) {
          supplierPayload.updates.push({
            view: data.view || "Plant",
            attribute_value: parseInt(attributeValue, 10),
            update_details: updateDetails
          });
        }
      });
    }

    // Only send the update if there are actual changes
    if (supplierPayload.updates.length === 0) {
      //console.log('No changes detected, not sending update');
      return Promise.resolve({ data: { message: 'No changes to update' } });
    }

    //console.log('Supplier update payload being sent:', JSON.stringify(supplierPayload, null, 2));
    // Use the same payload structure for PUT as POST, but only with changed rows
    return axios.put(`mdm/supplier-update/${id}`, supplierPayload, { headers });
  }
  if (type === 'customer') {
    // Exact payload format for supplier updates as specified
    const customerPayload = {
      profile_name: data.profile_name || "Global_customer",
      customer_name: data.customer_name || data.customer_description || "asd",
      effective_date: data.effective_date || new Date().toISOString().split('T')[0],
      internal_status: data.internal_status || "new",
      customer_category: data.customer_category || "Supplier",
      approval_status: data.approval_status,
      header_update_type: data.header_update_type || "CREATE",
      // Always include customer_number, even if it's an empty string
      customer_number: data.customer_number || "",
      image_url: data.image_url || "",
      updates: []
    };

    // Helper function to filter out empty values from an object
    const filterEmptyValues = (obj) => {
      if (!obj || typeof obj !== 'object') return {};

      const result = {};
      Object.entries(obj).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          result[key] = value;
        }
      });
      return result;
    };

    // Helper function to process operations and filter out empty values
    const processOperations = (operations) => {
      if (!Array.isArray(operations)) return [];

      return operations
        .map(op => {
          // Handle new format: operations with data property (no operation field)
          if (op.data && !op.operation) {
            // Check if this is a delete operation with delete_row property
            if (op.delete_row === true) {
              const pressedIcon = localStorage.getItem('pressedIcon');
              if (data.header_update_type === 'CREATE' || pressedIcon === 'createButton') {
                return null;
              }
              return { data: op.data || {}, delete_row: true };
            }
            const filteredData = filterEmptyValues(op.data);
            return Object.keys(filteredData).length > 0 ? { data: filteredData } : null;
          }

          // For add_row operations
          if (op.operation === 'add_row' && op.data) {
            const filteredData = filterEmptyValues(op.data);
            return Object.keys(filteredData).length > 0 ? { data: filteredData } : null;
          }

          // For update_row operations
          if (op.operation === 'update_row') {
            const updates = op.updates || op.data;
            if (!updates) return null;

            const filteredUpdates = filterEmptyValues(updates);
            return Object.keys(filteredUpdates).length > 0 ?
              { row_id: op.row_id, updates: filteredUpdates } : null;
          }

          // For delete operations
          if (op.operation === 'delete_row') {
            const pressedIcon = localStorage.getItem('pressedIcon');
            if (data.header_update_type === 'CREATE' || pressedIcon === 'createButton') {
              return null;
            }
            return {
              data: op.data || {},
              delete_row: true
            };
          }

          if (op.operation === 'delete_field' && op.field_name) {
            return { row_id: op.row_id, field_name: op.field_name };
          }

          return null;
        })
        .filter(item => item !== null); // Remove null items
    };

    // Handle the updates array structure
    if (data.updates && Array.isArray(data.updates)) {
      // Data is already in the correct format with updates array
      customerPayload.updates = data.updates.map(update => {
        // Extract operations from update_details (already an array)
        const operations = Array.isArray(update.update_details)
          ? update.update_details
          : [];

        // Process operations to only include changed items
        const updateDetails = processOperations(operations);

        return {
          view: update.view,
          attribute_value: update.attribute_value,
          update_details: updateDetails.length > 0 ? updateDetails : []
        };
      }).filter(update => update.update_details.length > 0); // Filter out empty updates
    } else if (data.update_details && Array.isArray(data.update_details.view_data_operations)) {
      // Group operations by attribute_value
      const operationsByAttribute = {};

      // Include all operations, including delete operations
      data.update_details.view_data_operations.forEach(op => {
        // Skip operations with empty data, but always include delete operations
        if (op.operation === 'add_row' && (!op.data || Object.keys(filterEmptyValues(op.data)).length === 0)) {
          return;
        }
        if (op.operation === 'update_row' &&
          (!op.updates || Object.keys(filterEmptyValues(op.updates || op.data)).length === 0)) {
          return;
        }
        // Always include delete operations regardless of data content
        // (no early return for delete operations)

        const attributeValue = op.attribute_value || 0;
        if (!operationsByAttribute[attributeValue]) {
          operationsByAttribute[attributeValue] = [];
        }
        operationsByAttribute[attributeValue].push(op);
      });

      // Create updates array from grouped operations
      Object.keys(operationsByAttribute).forEach(attributeValue => {
        const operations = operationsByAttribute[attributeValue];
        const updateDetails = processOperations(operations);

        // Only add to updates if there are actual changes
        if (updateDetails.length > 0) {
          customerPayload.updates.push({
            view: data.view || "Plant",
            attribute_value: parseInt(attributeValue, 10),
            update_details: updateDetails
          });
        }
      });
    }

    // Only send the update if there are actual changes
    if (customerPayload.updates.length === 0) {
      return Promise.resolve({ data: { message: 'No changes to update' } });
    }
    return axios.put(`mdm/customer-update/${id}`, customerPayload, { headers });
  }
  else {
    // console.log('Material update - Received data structure:', {
    //   hasUpdates: !!data.updates,
    //   updatesLength: data.updates?.length,
    //   hasUpdateDetails: !!data.update_details,
    //   hasViewDataOperations: !!data.update_details?.view_data_operations,
    //   viewDataOperationsLength: data.update_details?.view_data_operations?.length
    // });

    let materialPayload = { ...data };

    const filterEmptyValues = (obj) => {
      if (!obj || typeof obj !== 'object') return {};
      const result = {};
      Object.entries(obj).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          result[key] = value;
        }
      });
      return result;
    };

    const processOperations = (operations) => {
      if (!Array.isArray(operations)) return [];

      return operations
        .map(op => {
          if (op.data && !op.operation) {
            if (op.delete_row === true) {
              const pressedIcon = localStorage.getItem('pressedIcon');
              if (data.header_update_type === 'CREATE' || pressedIcon === 'createButton') {
                return null;
              }
              return { data: op.data || {}, delete_row: true };
            }
            const filteredData = filterEmptyValues(op.data);
            return Object.keys(filteredData).length > 0 ? { data: filteredData } : null;
          }

          if (op.operation === 'add_row' && op.data) {
            const filteredData = filterEmptyValues(op.data);
            return Object.keys(filteredData).length > 0 ? { data: filteredData } : null;
          }

          if (op.operation === 'update_row') {
            const updates = op.updates || op.data;
            if (!updates) return null;

            const filteredUpdates = filterEmptyValues(updates);
            return Object.keys(filteredUpdates).length > 0 ?
              { row_id: op.row_id, updates: filteredUpdates } : null;
          }

          if (op.operation === 'delete_row') {
            const pressedIcon = localStorage.getItem('pressedIcon');
            if (data.header_update_type === 'CREATE' || pressedIcon === 'createButton') {
              return null;
            }
            return {
              data: op.data || {},
              delete_row: true
            };
          }

          if (op.operation === 'delete_field' && op.field_name) {
            return { row_id: op.row_id, field_name: op.field_name };
          }

          return null;
        })
        .filter(item => item !== null);
    };

    if (data.updates && Array.isArray(data.updates)) {
      materialPayload.updates = data.updates.map(update => {
        const operations = Array.isArray(update.update_details)
          ? update.update_details
          : [];

        const updateDetails = processOperations(operations);

        return {
          view: update.view,
          attribute_value: update.attribute_value,
          update_details: updateDetails.length > 0 ? updateDetails : []
        };
      }).filter(update => update.update_details.length > 0);

      if (materialPayload.updates.length === 0) {
        //console.log('No changes detected, not sending update');
        return Promise.resolve({ data: { message: 'No changes to update' } });
      }

      //console.log('Material update payload being sent:', JSON.stringify(materialPayload, null, 2));
      return axios.put(`mdm/material-update/${id}`, materialPayload, { headers });
    }
    else if (data.update_details && Array.isArray(data.update_details.view_data_operations)) {
      const modifiedData = {
        ...data,
        update_details: {
          ...data?.update_details,
          view_data_operations: data?.update_details?.view_data_operations
            ?.filter(op => {
              if (op.operation === 'add_row' && (!op.data || Object.keys(op.data).length === 0)) {
                return false;
              }
              if (op.operation === 'update_row' &&
                (!op.updates || Object.keys(op.updates || op.data).length === 0)) {
                return false;
              }
              return true;
            })
            ?.map(op => ({
              ...op,
              update_type: 'CREATE'
            }))
        }
      };

      if (!modifiedData.update_details.view_data_operations ||
        modifiedData.update_details.view_data_operations.length === 0) {
        // console.log('No changes detected, not sending update');
        return Promise.resolve({ data: { message: 'No changes to update' } });
      }

      return axios.put(`mdm/material-update/${id}`, modifiedData, { headers });
    } else {
      //console.log('No valid update format detected, not sending update');
      return Promise.resolve({ data: { message: 'No changes to update' } });
    }
  }
};

const deleteMasterDataUpdateAPI = (id, type) => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk')
  };
  if (type === 'material')
    return axios.delete(`mdm/material-update/${id}`, { headers });
  else if (type === 'customer')
    return axios.delete(`mdm/customer-update/${id}`, { headers });
  else
    return axios.delete(`mdm/supplier-update/${id}`, { headers });
};

// Action creators
export const getMaterialUpdates = (page = 1, limit = 10, type) => {
  return (dispatch) => {
    dispatch({ type: FETCH_MATERIAL_UPDATES_START });
    return getMaterialUpdatesAPI(page, limit, type)
      .then(response => {
        dispatch({
          type: FETCH_MATERIAL_UPDATES_SUCCESS,
          payload: response.data.response
        });
        //console.log("response", response)
        return response.data.response;

      })
      .catch(error => {
        const errorMessage = error.response?.data?.message || 'Failed to fetch material updates';
        globalToast.showError(errorMessage);
        dispatch({
          type: FETCH_MATERIAL_UPDATES_FAIL,
          payload: errorMessage
        });
        throw error;
      });
  };
};
const getMaterialUpdateAPI = (id, type) => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk')
  };
  if (type === 'material') {
    return axios.get(`mdm/supplier-update/${id}`, { headers });
  } else {
    return axios.get(`mdm/supplier-update/${id}`, { headers });
  }
};



export const getMaterialUpdate = (id, type = 'material') => {
  return (dispatch) => {
    dispatch({ type: FETCH_SINGLE_MATERIAL_UPDATE_START });
    return getMaterialUpdateAPI(id, type)
      .then(response => {
        dispatch({
          type: FETCH_SINGLE_MATERIAL_UPDATE_SUCCESS,
          payload: response.data.response
        });
        return response.data.response;
      })
      .catch(error => {
        const errorMessage = error.response?.data?.message || 'Failed to fetch material update';
        globalToast.showError(errorMessage);
        dispatch({
          type: FETCH_SINGLE_MATERIAL_UPDATE_FAIL,
          payload: errorMessage
        });
        throw error;
      });
  };
};

export const createMaterialUpdate = (data, type = 'material', method = 'POST', redirectPath, id) => {
  return async (dispatch) => {
    dispatch({ type: CREATE_MATERIAL_UPDATE_START });
    try {
      const response = await createMaterialUpdateAPI(data, type, method, id);
      dispatch({ type: CREATE_MATERIAL_UPDATE_SUCCESS, payload: response.data });
      const logNumber = response?.data?.log_number;
      if (logNumber) {
        await handleExecutionLog(dispatch, logNumber);
        if (redirectPath) {
          await new Promise((resolve) => setTimeout(resolve, 300));
          history.push(redirectPath);
        }
      } else {
        globalToast.showSuccess(response?.data?.message || 'Saved successfully');
        if (redirectPath) {
          history.push(redirectPath);
        }
      }
      return response.data;
    } catch (error) {
      dispatch({ type: CREATE_MATERIAL_UPDATE_FAIL, payload: error.response?.data });
      if (!error.response?.data?.log_number) {
        const messages = error.response?.data?.message;
        if (Array.isArray(messages)) {
          dispatch({ type: SET_MESSAGE_DIALOG_DATA, payload: messages });
        } else {
          globalToast.showError(messages || 'Failed to save. Please try again.');
        }
      }
    }
  };
};

const createMaterialUpdateAPI = (data, type, method = 'POST', id) => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk'),
    'Content-Type': 'application/json'
  };

  if (type === 'supplier') {
    // Exact payload format for supplier updates as specified

    return method.toUpperCase() === 'PUT'
      ? axios.put('mdm/supplier-update' + `/${id}`, data, { headers })
      : axios.post('mdm/supplier-update', data, { headers });
  }
  else if (type === 'customer') {

    return method.toUpperCase() === 'PUT'
      ? axios.put('mdm/customer-update' + `/${id}`, data, { headers })
      : axios.post('mdm/customer-update', data, { headers });
  }
  else {
    return method.toUpperCase() === 'PUT'
      ? axios.put('mdm/material-update' + `/${id}`, data, { headers })
      : axios.post('mdm/material-update', data, { headers });
  }
};







export const updateMaterialUpdate = (id, data, type = 'material', redirectPath) => {
  return async (dispatch) => {
    dispatch({ type: UPDATE_MATERIAL_UPDATE_START });
    try {
      const response = await updateMaterialUpdateAPI(id, data, type);
      dispatch({ type: UPDATE_MATERIAL_UPDATE_SUCCESS, payload: response.data });
      const logNumber = response?.data?.log_number;
      if (logNumber) {
        await handleExecutionLog(dispatch, logNumber);
        if (redirectPath) {
          history.push(redirectPath);
        }
      } else {
        globalToast.showSuccess(response?.data?.message || 'Updated successfully');
        if (redirectPath) {
          history.push(redirectPath);
        }
      }
      return response.data;
    } catch (error) {
      dispatch({ type: UPDATE_MATERIAL_UPDATE_FAIL, payload: error.response?.data });
      if (!error.response?.data?.log_number) {
        const messages = error.response?.data?.message;
        if (Array.isArray(messages)) {
          dispatch({ type: SET_MESSAGE_DIALOG_DATA, payload: messages });
        } else {
          globalToast.showError(messages || 'Failed to update. Please try again.');
        }
      }
    }
  };
};

// Action to clear the current update in the Redux store
export const clearCurrentUpdate = () => {
  return {
    type: CLEAR_CURRENT_UPDATE
  };
};

export const clearCurrentUpdateRequest = () => {
  return {
    type: CLEAR_FETCH_CUSTOMER_MASTER_BY_ID
  };
};

// Action to clear the supplier updates cache in the Redux store
export const clearMasterDataUpdatesCache = () => {
  return {
    type: CLEAR_MASTER_DATA_UPDATES_CACHE
  };
};


export const deleteMasterDataUpdate = (id, type = 'material') => {
  return async (dispatch) => {
    dispatch({ type: DELETE_MATERIAL_UPDATE_START });
    try {
      const response = await deleteMasterDataUpdateAPI(id, type);
      dispatch({ type: DELETE_MATERIAL_UPDATE_SUCCESS, payload: response.data });

      const logNumber = response?.data?.log_number;
      if (logNumber) {
        const { hasError } = await handleExecutionLog(dispatch, logNumber);
        return { success: !hasError, response };
      } else {
        globalToast.showSuccess(response?.data?.message || 'Deleted successfully');
        return { success: true, response };
      }
    } catch (error) {
      dispatch({
        type: DELETE_MATERIAL_UPDATE_FAIL,
        payload: { message: error.response?.data?.message || 'Failed to delete material' },
      });

      if (error.response?.data?.log_number) {
        return { success: false };
      }
      else {
        const messages = error.response?.data?.message;
        if (Array.isArray(messages)) {
          dispatch({ type: SET_MESSAGE_DIALOG_DATA, payload: messages });
        } else {
          globalToast.showError(messages || `Failed to delete ${capitalizeFirstLetter(type)} '${id}'. Please try again.`);
        }
        return { success: false };
      }
    }
  };
};

export const getMasterDataUpdatesByProfile = (profile, type) => {
  if (!profile) {
    try {
      const stored = localStorage.getItem("selectedProfile");
      if (stored) {
        const parsed = JSON.parse(stored);
        profile = parsed?.profile || stored;
      }
    } catch (e) {
      profile = localStorage.getItem("selectedProfile");
    }
  }
  // console.log('authaction profile:', profile)
  return (dispatch) => {
    dispatch({ type: FETCH_MATERIAL_UPDATES_START });
    return getMasterDataUpdatesByProfileAPI(profile, type)
      .then(response => {
        dispatch({
          type: FETCH_MASTER_DATA_UPDATES_BY_PROFILE,
          payload: response.data.response
        });
        return response.data.response;

      })
      .catch(error => {
        const errorMessage = error.response?.data?.message || 'Failed to fetch material';
        globalToast.showError(errorMessage);
        dispatch({
          type: FETCH_MASTER_DATA_UPDATES_BY_PROFILE_FAIL,
          payload: errorMessage
        });
        throw error;
      });
  };
};
export const getMasterDataUpdatesByProfileAPI = (profile, type) => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk')
  };
  if (type)
    return axios.get(`mdm/${type}-update?profileName=${profile}`, { headers });
  else
    return axios.get(`mdm/supplier-update?profileName=${profile}`, { headers });
};

export const getSupplierDetailsByUpdateNo = (updateNo, type) => {
  return (dispatch) => {
    dispatch({ type: FETCH_MATERIAL_UPDATES_START });
    return getSupplierDetailsByUpdateNoAPI(updateNo, type)
      .then(response => {
        dispatch({
          type: SUPPLIER_DETAILS_BY_UPDATE_NO,
          payload: response.data.response
        });
        return response.data.response;

      })
      .catch(error => {
        const errorMessage = error.response?.data?.message || 'Failed to fetch material';
        globalToast.showError(errorMessage);
        dispatch({
          type: SUPPLIER_DETAILS_BY_UPDATE_NO_FAIL,
          payload: errorMessage
        });
        throw error;
      });
  };
};
export const getSupplierDetailsByUpdateNoAPI = (updateNo, type) => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk')
  };
  if (type !== 'supplier')
    return axios.get(`mdm/${type}-update?headerId=${updateNo}`, { headers });
  else
    return axios.get(`mdm/supplier-update?headerId=${updateNo}`, { headers });
};

const getCustomerMasterAPI = () => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk'),
  };
  return axios.get('mdm/customer-master?get_all=true', { headers });
};

const getCustomerMasterGroupedAPI = (master = "customer") => {
  const headers = {
    Authorization: secureLocalStorage.getItem("aTk"),
  };

  return axios.get(`mdm/${master}-master?group_by=${master}_number`, { headers });
};
export const getCustomerMaster = () => {
  return (dispatch) => {
    dispatch({ type: FETCH_CUSTOMER_MASTER_START });
    return getCustomerMasterAPI()
      .then((response) => {
        dispatch({
          type: FETCH_CUSTOMER_MASTER_SUCCESS,
          payload: response.data.response || response.data,
        });
        return response.data.response || response.data;
      })
      .catch((error) => {
        const errorMessage =
          error.response?.data?.message ||
          'Failed to fetch customer master data';
        globalToast.showError(errorMessage);
        dispatch({
          type: FETCH_CUSTOMER_MASTER_FAIL,
          payload: errorMessage,
        });
        throw error;
      });
  };
};

export const getCustomerMasterGrouped = (page = "customer") => {
  return (dispatch) => {
    //console.log('getCustomerMasterGrouped action called');
    dispatch({ type: FETCH_CUSTOMER_MASTER_GROUPED_START });
    return getCustomerMasterGroupedAPI(page)
      .then((response) => {
        dispatch({
          type: FETCH_CUSTOMER_MASTER_GROUPED_SUCCESS,
          payload: response.data.response || response.data,
        });
        return response.data.response || response.data;
      })
      .catch((error) => {
        const errorMessage =
          error.response?.data?.message ||
          'Failed to fetch grouped customer master data';
        globalToast.showError(errorMessage);
        dispatch({
          type: FETCH_CUSTOMER_MASTER_GROUPED_FAIL,
          payload: errorMessage,
        });
        throw error;
      });
  };
};

// Supplier Master API functions
const getMasterDataApprovalByIdAPI = (id, page = "customer") => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk'),
  };

  // Encode the id so any leading/trailing whitespace (e.g. "123 ") is preserved
  // and sent to the API as %20 instead of being dropped by the URL parser.
  const encodedId = encodeURIComponent(id);
  let url = "";
  if (page === "material") {
    url = `mdm/material-master?material_number=${encodedId}`;
  }
  else if (page === "supplier") {
    url = `mdm/supplier-master?supplier_number=${encodedId}`;
  } else {
    // default customer
    url = `mdm/customer-master?customer_number=${encodedId}`;
  }

  return axios.get(url, { headers });
};

export const getMasterDataApprovalById = (id, page = "customer") => {
  return (dispatch) => {
    dispatch({ type: FETCH_CUSTOMER_MASTER_BY_ID_START });
    return getMasterDataApprovalByIdAPI(id, page)
      .then((response) => {
        dispatch({
          type: FETCH_CUSTOMER_MASTER_BY_ID_SUCCESS,
          payload: response.data.response || response.data,
        });
        return response.data.response || response.data;
      })
      .catch((error) => {
        const errorMessage =
          error.response?.data?.message ||
          'Failed to fetch customer master data';
        globalToast.showError(errorMessage);
        dispatch({
          type: FETCH_CUSTOMER_MASTER_BY_ID_FAIL,
          payload: errorMessage,
        });
        throw error;
      });
  };
};

// Customer Update with Default Profile API
const getCustomerUpdateDefaultProfileAPI = () => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk'),
  };
  // console.log(
  //   'getCustomerUpdateDefaultProfileAPI - Making API call to:',
  //   'mdm/customer-update?defaultProfile=true',
  // );
  return axios.get('mdm/customer-update?default_profile=true', { headers });
};

export const getCustomerUpdateDefaultProfile = () => {
  return (dispatch) => {
    dispatch({ type: FETCH_CUSTOMER_UPDATE_DEFAULT_PROFILE_START });
    return getCustomerUpdateDefaultProfileAPI()
      .then((response) => {
        dispatch({
          type: FETCH_CUSTOMER_UPDATE_DEFAULT_PROFILE_SUCCESS,
          payload: response.data.response || response.data,
        });
        return response.data.response || response.data;
      })
      .catch((error) => {
        const errorMessage =
          error.response?.data?.message ||
          'Failed to fetch customer update data';
        globalToast.showError(errorMessage);
        dispatch({
          type: FETCH_CUSTOMER_UPDATE_DEFAULT_PROFILE_FAIL,
          payload: errorMessage,
        });
        throw error;
      });
  };
};

export const getApprovalPageDetailsAPI = () => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk'),
  };
  return axios.get('api/config/workflow-mass-approval-data', { headers });
};

export const getApprovalPageDetails = () => {
  return (dispatch) => {
    dispatch({ type: FETCH_APPROVAL_PAGE_DETAILS });
    return getApprovalPageDetailsAPI()
      .then((response) => {
        dispatch({
          type: FETCH_APPROVAL_PAGE_DETAILS,
          payload: response.data.response || response.data,
        });
        return response.data.response || response.data;
      });
  };
};

const getMasterGroupedAPI = (master) => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk'),
  };
  //console.log(`getMasterGroupedAPI - Making API call to: mdm/${master}-master?group_by=${master}_number`);
  return axios.get(`mdm/${master}-master?group_by=${master}_number`, { headers });
};

export const getMasterGrouped = (master = 'customer') => {
  return (dispatch) => {
    dispatch({ type: FETCH_CUSTOMER_MASTER_GROUPED_START });
    return getMasterGroupedAPI(master)
      .then((response) => {
        dispatch({
          type: FETCH_CUSTOMER_MASTER_GROUPED_SUCCESS,
          payload: response.data.response || response.data,
        });
        return response.data.response || response.data;
      })
      .catch((error) => {
        const errorMessage =
          error.response?.data?.message ||
          `Failed to fetch grouped ${master} master data`;
        globalToast.showError(errorMessage);
        dispatch({
          type: FETCH_CUSTOMER_MASTER_GROUPED_FAIL,
          payload: errorMessage,
        });
        throw error;
      });
  };
};
const getMasterApprovalByIdAPI = (id, master) => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk'),
  };
  // console.log(
  //   `getMasterApprovalByIdAPI - Making API call to: mdm/${master}-master?${master}_number=${id}`,
  // );
  return axios.get(`mdm/${master}-master`, {
    params: { [`${master}_number`]: id },
    headers,
  });
};

export const getMasterApprovalById = (id, master = 'customer') => {
  return (dispatch) => {
    dispatch({ type: FETCH_CUSTOMER_MASTER_BY_ID_START });
    return getMasterApprovalByIdAPI(id, master)
      .then((response) => {
        dispatch({
          type: FETCH_CUSTOMER_MASTER_BY_ID_SUCCESS,
          payload: response.data.response || response.data,
        });
        return response.data.response || response.data;
      })
      .catch((error) => {
        const errorMessage =
          error.response?.data?.message ||
          `Failed to fetch ${master} master data`;
        globalToast.showError(errorMessage);
        dispatch({
          type: FETCH_CUSTOMER_MASTER_BY_ID_FAIL,
          payload: errorMessage,
        });
        throw error;
      });
  };
};

const getMasterUpdateDefaultProfileAPI = (master) => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk'),
  };
  // console.log(
  //   `getMasterUpdateDefaultProfileAPI - Making API call to: mdm/${master}-update?default_profile=true`,
  // );
  return axios.get(`mdm/${master}-update?default_profile=true`, { headers });
};
export const getMasterUpdateDefaultProfile = (master = 'customer') => {
  return (dispatch) => {
    dispatch({ type: FETCH_CUSTOMER_UPDATE_DEFAULT_PROFILE_START });
    return getMasterUpdateDefaultProfileAPI(master)
      .then((response) => {
        dispatch({
          type: FETCH_CUSTOMER_UPDATE_DEFAULT_PROFILE_SUCCESS,
          payload: response.data.response || response.data,
        });
        return response.data.response || response.data;
      })
      .catch((error) => {
        const errorMessage =
          error.response?.data?.message ||
          `Failed to fetch ${master} update data`;
        globalToast.showError(errorMessage);
        dispatch({
          type: FETCH_CUSTOMER_UPDATE_DEFAULT_PROFILE_FAIL,
          payload: errorMessage,
        });
        throw error;
      });
  };
};

// Material Master specific wrapper functions
export const getMaterialMasterGrouped = () => {
  return getMasterGrouped('material');
};

export const getMaterialUpdateDefaultProfile = () => {
  return getMasterUpdateDefaultProfile('material');
};

// Supplier Master specific wrapper functions
export const getSupplierMasterGrouped = () => {
  return getMasterGrouped('supplier');
};

export const getSupplierUpdateDefaultProfile = () => {
  return getMasterUpdateDefaultProfile('supplier');
};

// Category attribute values for the MDM tile gallery
const getCategoryAttributeValuesAPI = (attributeName) => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk'),
  };
  return axios.post(
    '/mdm/attribute-values',
    {
      attribute_name: attributeName,
      application_type: 'Global',
      limit: 500,
    },
    { headers },
  );
};

export const getCategoryAttributeValues = (attributeName) => {
  return (dispatch) => {
    dispatch(fetchStart());
    return getCategoryAttributeValuesAPI(attributeName)
      .then((response) => {
        dispatch(fetchEnd());
        return response.data?.response || [];
      })
      .catch((error) => {
        dispatch(fetchEnd());
        const errorMessage =
          error.response?.data?.message ||
          `Failed to fetch ${attributeName} values`;
        globalToast.showError(errorMessage);
        throw error;
      });
  };
};

// Master Data Profile (moved from OperationalReports/AuthAction)
export const clearMasterDataProfile = () => {
  return { type: CLEAR_MASTER_DATA_PROFILE };
};

export const getMasterDataProfile = (type = 'material', clearData = true) => {
  return (dispatch) => {
    // clearData=false keeps the current rows on screen while refetching (e.g.
    // an in-place refresh after delete/clone) so the grid doesn't blank out.
    // Default true preserves the cross-page clear on initial mounts.
    if (clearData) {
      dispatch({ type: CLEAR_MASTER_DATA_PROFILE });
    }
    dispatch(fetchStart());
    getMasterDataProfileAPI(type)
      .then((response) => {
        dispatch(getMasterDataProfileSuccess(response.data.response));
        dispatch(fetchEnd());
      })

      .catch((error) => {
        dispatch(fetchEnd());
      });
  };
};

const getMasterDataProfileAPI = (type = 'material') => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk'),
  };
  var result;
  if (type == 'supplier') {
    result = axios.get('mdm/supplier-master/profile', {
      headers,
    });
  } else if (type == 'customer') {
    result = axios.get('/mdm/customer-master/profile', {
      headers,
    });
  } else if (type == 'employee') {
    result = axios.get('mdm/employee-master-design?groupBy=profile', {
      headers,
    });
  } else {
    result = axios.get('mdm/material-master/profile', {
      headers,
    });
  }
  return result;
};

export const getMasterDataProfileSuccess = (data) => {
  return {
    type: GET_MASTER_DATA_PROFILE,
    data,
  };
};

// Apply master data updates (moved from Attributes/AuthAction)
export const postMasterDataUpdate = (id, profile, type) => {
  return async (dispatch) => {
    dispatch(fetchStart());
    try {
      const response = await postMasterDataUpdateApi(id, profile, type);
      dispatch(fetchEnd());
      const logNumber = response?.data?.log_number;
      if (logNumber) {
        const { hasError } = await handleExecutionLog(dispatch, logNumber);
        // dispatch(getMasterDataUpdatesByProfile(profile, type));
        return { success: !hasError, response };
      } else {
        globalToast.showSuccess(response?.data?.message || 'Posted successfully');
        // dispatch(getMasterDataUpdatesByProfile(profile, type));
        return { success: true, response };
      }
    } catch (error) {
      dispatch(fetchEnd());
      if (error.response?.data?.log_number) {
        return { success: false };
      } else {
        const messages = error.response?.data?.message;
        if (Array.isArray(messages)) {
          dispatch({ type: SET_MESSAGE_DIALOG_DATA, payload: messages });
        } else {
          globalToast.showError(messages || `Failed to post ${type} update. Please try again.`);
        }
        return { success: false };
      }
    }
  };
};

const postMasterDataUpdateApi = (id, profile, type) => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk'),
  };
  const payload = {
    id: id
  };
  return axios.post(`mdm/apply-${type}-updates`, payload, { headers });
};

// Requests in flight, keyed by master + profile, so the pages that mount back
// to back during a navigation (gallery -> updates list -> add) share one call.
const profileByNameRequests = {};

const profileByNameKey = (profile, type) => `${type || ''}|${profile}`;

// The profile definition is fetched once per (master, profile) selection: on a
// cache hit the stored definition is returned as-is, and while a request is in
// flight every caller gets the same promise. Callers therefore do not need to
// guard the fetch themselves.
export const getMasterDataProfileByName = (profile, type, silent = false) => {
  return (dispatch, getState) => {
    const key = profileByNameKey(profile, type);
    const masterData = getState().masterData || {};
    // The key is only set after a successful fetch, so matching it means the
    // definition in the store belongs to this master + profile.
    if (masterData.masterDataProfileByNameKey === key) {
      return Promise.resolve(masterData.masterDataProfileByNameData);
    }
    if (profileByNameRequests[key]) return profileByNameRequests[key];

    if (!silent) dispatch(fetchStart());
    const request = getMasterDataProfileByNameAPI(profile, type)
      .then((response) => {
        delete profileByNameRequests[key];
        if (!silent) dispatch(fetchEnd());
        dispatch(getMasterDataProfileByNameSuccess(response.data.response, key));
        return response.data.response;
      })
      .catch((error) => {
        delete profileByNameRequests[key];
        if (!silent) dispatch(fetchEnd());
        console.error('Error getting master data profile by name:', error);
        throw error;
      });
    profileByNameRequests[key] = request;
    return request;
  };
};

export const getMasterDataProfileByNameSuccess = (data, key = '') => {
  return {
    type: GET_MASTER_DATA_PROFILE_BY_NAME_SUCCESS,
    data,
    key,
  };
};

const getMasterDataProfileByNameAPI = (profile, type) => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk'),
  };
  if (type)
    return axios.get(`mdm/${type}-master/profile-by-name/${profile}`, {
      headers: headers,
    });
  else
    return axios.get(`mdm/supplier-master-design?profile=${profile}`, {
      headers: headers,
    });
};

// Master Data by profile (moved from OperationalReports/AuthAction)
export const getMasterDataByProfile = (profile, type) => {
  return (dispatch) => {
    return getMasterDataByProfileAPI(profile, type)
      .then((response) => {
        dispatch(getMasterDataByProfileSuccess(response.data.response));
        return response.data.response;
      })
      .catch((error) => {
        throw error;
      });
  };
};
const getMasterDataByProfileAPI = (profile, type) => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk'),
  };
  return axios.get(`mdm/${type}-master/profile-by-name/${profile}`, {
    headers,
  });
};
export const getMasterDataByProfileSuccess = (data) => {
  return {
    type: GET_CUSTOMER_BY_PROFILE_SUCCESS,
    data,
  };
};

export const clearCustomerByProfile = () => {
  return { type: CLEAR_CUSTOMER_BY_PROFILE };
};

// Clone customer master profile (moved from OperationalReports/AuthAction)
export const cloneCustomerMaster = (data) => {
  return (dispatch) => {
    return cloneCustomerMasterAPI(data)
      .then((response) => {
        dispatch(cloneCustomerMasterSuccess(response.data.response));

        const messageArray = response.data?.message;
        if (Array.isArray(messageArray)) {
          messageArray.forEach((msgObj) => {
            if (msgObj.message_type === 'success') {
              globalToast.showSuccess(msgObj.message);
            } else if (msgObj.message_type === 'error') {
              globalToast.showError(msgObj.message);
            } else if (msgObj.message_type === 'warning') {
              globalToast.showWarning(msgObj.message);
            } else {
              globalToast.showInfo(msgObj.message);
            }
          });
        }

        return response.data.response;
      })
      .catch((error) => {
        console.error('Error cloning customer master:', error);

        const errMessage =
          error?.response?.data?.message?.message ||
          error?.response?.data?.message ||
          'Failed to clone customer profile';

        globalToast.showError(errMessage);
        throw error;
      });
  };
};

export const cloneCustomerMasterSuccess = (data) => {
  return {
    type: CLONE_CUSTOMER_MASTER_SUCCESS,
    data,
  };
};

const cloneCustomerMasterAPI = (data) => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk'),
  };

  return axios.post(`mdm/customer-master-design/copy-profile`, data, {
    headers,
  });
};

// Master Data Config - Add / Edit / Delete (moved from OperationalReports/AuthAction)
const MASTER_DATA_ROUTE_MAP = {
  customer: '/customer-master-configuration-page',
  material: '/material-master-configuration-page',
  supplier: '/supplier-master-configuration-page',
};

export const createMasterDataConfig = (data, id, page, type) => {
  return async (dispatch) => {
    dispatch(fetchStart());
    try {
      const response = await createMasterDataConfigAPI(data, id, page, type);
      dispatch(fetchEnd());
      const logNumber = response?.data?.log_number;
      if (logNumber) {
        await handleExecutionLog(dispatch, logNumber);
        if (type === 'delete') {
          // const { hasError } = await handleExecutionLog(dispatch, logNumber);
          dispatch(getMasterDataProfile(page, false));
        } else {
          await new Promise((resolve) => setTimeout(resolve, 400));
          history.push(MASTER_DATA_ROUTE_MAP[page] || MASTER_DATA_ROUTE_MAP.customer);
        }
      } else {
        const successMessage = response?.data?.message;
        if (type === 'delete') {
          if (successMessage) globalToast.showSuccess(successMessage);
          dispatch(getMasterDataProfile(page, false));
        } else {
          if (successMessage) globalToast.showSuccess(successMessage);
          history.push(MASTER_DATA_ROUTE_MAP[page] || MASTER_DATA_ROUTE_MAP.customer);
        }
      }
      return response.data;
    } catch (error) {
      dispatch(fetchEnd());
      if (error.response?.data?.log_number) {
        // Refresh the profile list on delete so the grid reflects the actual
        // backend state even when the delete failed (mirrors the try block).
        if (type === 'delete') {
          dispatch(getMasterDataProfile(page, false));
        }
        return { success: false };
      } else {
        const messages = error.response?.data?.message;
        if (Array.isArray(messages)) {
          dispatch({ type: SET_MESSAGE_DIALOG_DATA, payload: messages });
        } else {
          globalToast.showError(messages || `Failed to ${type || 'save'} configuration. Please try again.`);
        }
        if (type === 'delete') {
          dispatch(getMasterDataProfile(page, false));
        }
        return { success: false };
      }
    }
  };
};

const createMasterDataConfigAPI = (data, id, page, type) => {
  const headers = {
    Authorization: secureLocalStorage.getItem('aTk'),
  };
  const endpoint = `mdm/${page}-master/profile`;
  if (type === 'edit') {
    return axios.put(`${endpoint}/${id}`, data, { headers });
  } else if (type === 'delete') {
    return axios.delete(`${endpoint}/${id}`, { headers });
  } else {
    return axios.post(endpoint, data, { headers });
  }
};
